// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AskPunttDesktopNotificationsToggle.test.tsx
import { Flex, Switch, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUiPrefs } from "../hooks/useUiPrefs";
import {
  getDesktopNotificationPermission,
  isDesktopNotificationSupported,
  requestDesktopNotificationPermission,
} from "../utils/askPunttDesktopNotifications";

type AskPunttDesktopNotificationsToggleControlProps = {
  onToggle?: () => void;
};

export function AskPunttDesktopNotificationsToggleControl({
  onToggle,
}: AskPunttDesktopNotificationsToggleControlProps) {
  const { t } = useTranslation("ui");
  const { prefs, setPref } = useUiPrefs();
  const supported = isDesktopNotificationSupported();
  const [permission, setPermission] = useState(
    getDesktopNotificationPermission,
  );

  useEffect(() => {
    setPermission(getDesktopNotificationPermission());
  }, [prefs.askPunttDesktopNotifications]);

  const checked = prefs.askPunttDesktopNotifications === true;
  const denied = permission === "denied";
  const disabled = !supported || denied;

  const handleCheckedChange = useCallback(
    async (next: boolean) => {
      if (!supported) return;

      if (!next) {
        setPref("askPunttDesktopNotifications", false);
        onToggle?.();
        return;
      }

      const result = await requestDesktopNotificationPermission();
      setPermission(result === "unsupported" ? permission : result);
      if (result === "granted") {
        setPref("askPunttDesktopNotifications", true);
      } else {
        setPref("askPunttDesktopNotifications", false);
      }
      onToggle?.();
    },
    [onToggle, permission, setPref, supported],
  );

  return (
    <Flex align="center" justify="between" gap="3" width="100%">
      <Flex direction="column" gap="1">
        <Text size="2">{t("askPunttDesktopNotificationsToggle.title")}</Text>
        {denied ? (
          <Text size="1" color="gray">
            {t("askPunttDesktopNotificationsToggle.blockedInBrowserSettings")}
          </Text>
        ) : null}
        {!supported ? (
          <Text size="1" color="gray">
            {t("askPunttDesktopNotificationsToggle.notSupportedInBrowser")}
          </Text>
        ) : null}
      </Flex>
      <Switch
        data-testid="sidebar-settings-ask-puntt-notifications-switch"
        aria-label={t("askPunttDesktopNotificationsToggle.enableAriaLabel")}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => {
          void handleCheckedChange(next);
        }}
      />
    </Flex>
  );
}
